import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Pencil, Trash2, Briefcase, Clock, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import MediaUpload from '@/components/MediaUpload';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Services() {
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [open, setOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    duree: '',
    prix: '',
  });

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/services`, getAuthHeaders());
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        duree: parseInt(formData.duree),
        prix: parseFloat(formData.prix),
      };
      if (editingService) {
        await axios.put(`${API_URL}/api/services/${editingService.id}`, payload, getAuthHeaders());
        toast.success('Prestation modifiée');
      } else {
        await axios.post(`${API_URL}/api/services`, payload, getAuthHeaders());
        toast.success('Prestation créée');
      }
      fetchServices();
      handleClose();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/services/${deleteId}`, getAuthHeaders());
      toast.success('Prestation supprimée');
      fetchServices();
      setDeleteId(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      nom: service.nom,
      duree: service.duree.toString(),
      prix: service.prix.toString(),
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingService(null);
    setFormData({ nom: '', duree: '', prix: '' });
  };

  return (
    <div className="space-y-8" data-testid="services-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Prestations</h1>
          <p className="text-muted-foreground mt-2">Gérez vos services et tarifs</p>
        </div>
        <div className="flex items-center gap-4">
          <ToggleGroup type="single" value={viewMode} onValueChange={(v) => v && setViewMode(v)} data-testid="view-toggle">
            <ToggleGroupItem value="grid" aria-label="Vue grille">
              <LayoutGrid className="w-4 h-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="Vue liste">
              <List className="w-4 h-4" />
            </ToggleGroupItem>
          </ToggleGroup>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="btn-add-service">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle prestation
              </Button>
            </DialogTrigger>
          <DialogContent data-testid="service-dialog">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Modifier' : 'Nouvelle'} prestation</DialogTitle>
              <DialogDescription>
                Définissez votre prestation
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nom">Nom de la prestation *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Pose complète"
                  required
                  data-testid="input-nom"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="duree">Durée (minutes) *</Label>
                <Input
                  id="duree"
                  type="number"
                  value={formData.duree}
                  onChange={(e) => setFormData({ ...formData, duree: e.target.value })}
                  placeholder="Ex: 120"
                  required
                  data-testid="input-duree"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="prix">Prix (€) *</Label>
                <Input
                  id="prix"
                  type="number"
                  step="0.01"
                  value={formData.prix}
                  onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                  placeholder="Ex: 65.00"
                  required
                  data-testid="input-prix"
                />
              </div>
              
              {editingService && (
                <div className="pt-4 border-t">
                  <MediaUpload
                    serviceId={editingService.id}
                    media={editingService.media || []}
                    onMediaUpdate={async () => {
                      try {
                        // Recharger le service spécifique
                        const response = await axios.get(`${API_URL}/api/services/${editingService.id}`, getAuthHeaders());
                        setEditingService(response.data);
                        // Recharger aussi la liste complète
                        fetchServices();
                      } catch (error) {
                        console.error('Erreur rechargement:', error);
                      }
                    }}
                    getAuthHeaders={getAuthHeaders}
                  />
                </div>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>Annuler</Button>
                <Button type="submit" data-testid="btn-submit-service">Enregistrer</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {services.length === 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Briefcase className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucune prestation pour le moment</p>
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card className="rounded-xl border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Durée</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service) => (
                <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                  <TableCell className="font-medium">{service.nom}</TableCell>
                  <TableCell>{service.duree} min</TableCell>
                  <TableCell className="font-semibold text-primary">{service.prix.toFixed(2)} €</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(service)} data-testid={`btn-edit-${service.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteId(service.id)} className="text-destructive hover:text-destructive" data-testid={`btn-delete-${service.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <Card
              key={service.id}
              className="rounded-xl border-border/60 hover:border-primary/20 transition-colors"
              data-testid={`service-card-${service.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-accent/30 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-accent-foreground" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{service.nom}</h3>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>{service.duree} minutes</span>
                  </div>
                  <div className="text-2xl font-bold text-primary">
                    {service.prix.toFixed(2)} €
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t border-border/40">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(service)}
                    data-testid={`btn-edit-${service.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(service.id)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`btn-delete-${service.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La prestation sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancel-delete">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="btn-confirm-delete">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}